/**
 * Customer Intelligence Admin — three sections in one page:
 *   1. Health dashboard (sync status of Desk + CRM + Soul + SiteVault)
 *   2. Field-Mapping table (rename Zoho values without code deploys)
 *   3. Members table (search, edit, export to CSV)
 *
 * Mounts at /admin/ci. CEO-only writes; leaders can read.
 */
import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import MemberCareCheatGuide from '@/components/MemberCareCheatGuide';
import MCERunPanel from '@/components/MCERunPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
import {
  Activity, Database, Sparkles, HardDrive, RefreshCw,
  Edit2, Trash2, Plus, Download, Save, X, AlertTriangle,
  CheckCircle2, AlertCircle, Search, Heart, Send,
  GitMerge, ArrowRight,
} from 'lucide-react';

const FIELD_OPTIONS = [
  { v: 'pillar', label: 'Pillar (Zoho ticket category)' },
  { v: 'benefit', label: 'Benefit' },
  { v: 'category', label: 'Category (level 2)' },
  { v: 'sub_category', label: 'Sub-category (level 3)' },
  { v: 'member_tier', label: 'Member tier (model_name → RR/RRS)' },
];
const STATUS_COLOR = { green: '#1f5736', amber: '#A86B2C', red: '#A32D2D' };
const STATUS_BG    = { green: '#E5F4ED', amber: '#FFF4E0', red: '#FEE2E2' };

export default function CustomerIntelligenceAdmin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isCeo = (user?.role || '').toLowerCase() === 'ceo';
  const [clientId, setClientId] = useState('jlr');
  const [tab, setTab] = useState('health');

  if (!user) return null;
  if (!['ceo', 'leader'].includes((user.role || '').toLowerCase())) {
    return (
      <div className="p-8 text-center" style={{ color: '#A32D2D' }}>
        Customer Intelligence Admin is restricted to CEO + leaders.
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6" style={{ background: '#F4F1EB' }}>
      <div className="max-w-6xl mx-auto space-y-4">
        <div className="flex items-center gap-3 mb-2">
          <button onClick={() => navigate(-1)} data-testid="ci-admin-back" className="text-[12px] underline" style={{ color: '#6B7280' }}>← Back</button>
          <h1 className="text-2xl font-bold" style={{ color: '#1B2A4A' }}>Customer Intelligence — Admin</h1>
          <select
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            data-testid="ci-admin-client-select"
            className="ml-auto text-[12px] px-3 py-1.5 rounded border bg-white"
            style={{ borderColor: '#E5E1DC' }}>
            <option value="jlr">JLR</option>
            <option value="me">MindEscapes</option>
            <option value="aabe">AaBee Circle</option>
            <option value="dreamfolks">DreamFolks</option>
            <option value="xiaomi">Xiaomi</option>
          </select>
        </div>

        {/* Tab strip */}
        <div className="flex items-center gap-1 border-b" style={{ borderColor: '#E5E1DC' }} data-testid="ci-admin-tabs">
          <TabBtn active={tab === 'health'} onClick={() => setTab('health')} icon={<Activity size={12} />} testId="ci-admin-tab-health">Health</TabBtn>
          <TabBtn active={tab === 'mappings'} onClick={() => setTab('mappings')} icon={<Database size={12} />} testId="ci-admin-tab-mappings">Field Mappings</TabBtn>
          <TabBtn active={tab === 'aliases'} onClick={() => setTab('aliases')} icon={<GitMerge size={12} />} testId="ci-admin-tab-aliases">Picklist Merges</TabBtn>
          <TabBtn active={tab === 'members'} onClick={() => setTab('members')} icon={<Sparkles size={12} />} testId="ci-admin-tab-members">Members</TabBtn>
          <TabBtn active={tab === 'mce'} onClick={() => setTab('mce')} icon={<Heart size={12} />} testId="ci-admin-tab-mce">Member Care</TabBtn>
        </div>

        {tab === 'health' && <HealthPanel clientId={clientId} />}
        {tab === 'mappings' && <MappingsPanel clientId={clientId} isCeo={isCeo} />}
        {tab === 'aliases' && <PicklistAliasesPanel clientId={clientId} isCeo={isCeo} />}
        {tab === 'members' && <MembersPanel clientId={clientId} isCeo={isCeo} />}
        {tab === 'mce' && <MemberCarePanel clientId={clientId} isCeo={isCeo} />}
      </div>
    </div>
  );
}


function TabBtn({ active, onClick, icon, children, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className="px-3 py-2 text-[12px] font-medium inline-flex items-center gap-1.5 transition border-b-2"
      style={{ borderColor: active ? '#1C304A' : 'transparent',
        color: active ? '#1B2A4A' : '#6B7280', marginBottom: -1 }}>
      {icon} {children}
    </button>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// 1. HEALTH PANEL
// ─────────────────────────────────────────────────────────────────────────
function HealthPanel({ clientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/ci-health?client_id=${clientId}`);
      setData(r.data);
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  if (!data) return <p className="text-[12px] py-6" style={{ color: '#9CA3AF' }}>{loading ? 'Loading health…' : 'Failed to load.'}</p>;

  const fmtTime = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }) + ' IST'; } catch { return iso; }
  };

  return (
    <div className="space-y-3" data-testid="ci-admin-health">
      <div className="flex items-center gap-2">
        <h2 className="text-base font-semibold" style={{ color: '#1B2A4A' }}>Sync health · {clientId.toUpperCase()}</h2>
        <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase" style={{
          background: STATUS_BG[data.overall_status] || '#F3F4F6',
          color: STATUS_COLOR[data.overall_status] || '#6B7280',
        }}>{data.overall_status}</span>
        <a
          href={`${BACKEND_URL}/api/zoho/connect`}
          target="_blank" rel="noopener noreferrer"
          data-testid="ci-admin-zoho-reconnect"
          className="ml-auto text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border bg-white"
          style={{ borderColor: '#C9A66B', color: '#6B5439' }}
          title="Re-authorize Zoho — required when scopes change (e.g. adding CRM write permissions)">
          <RefreshCw size={11} /> Reconnect Zoho
        </a>
        <button onClick={load} data-testid="ci-admin-health-refresh" className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border bg-white" style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <HealthCard
          title="Zoho Desk · Tickets"
          status={data.zoho_desk.status}
          rows={[
            ['Last sync', fmtTime(data.zoho_desk.last_sync)],
            ['Tickets synced (last run)', data.zoho_desk.tickets_synced_last_run ?? '—'],
            ['Total tickets', (data.zoho_desk.total_tickets || 0).toLocaleString('en-IN')],
            ['Next run', data.zoho_desk.next_run],
          ]}
          testId="ci-health-desk"
        />
        <HealthCard
          title="Zoho CRM · Members"
          status={data.zoho_crm.status}
          rows={[
            ['Last sync', fmtTime(data.zoho_crm.last_sync)],
            ['Members synced (last run)', data.zoho_crm.contacts_synced_last_run ?? '—'],
            ['Total members', (data.zoho_crm.total_members || 0).toLocaleString('en-IN')],
            ['Soul Forms completed', `${(data.zoho_crm.soul_forms_completed || 0).toLocaleString('en-IN')} (${data.zoho_crm.soul_form_pct ?? 0}%)`],
            ['DOB on file', `${data.zoho_crm.dob_pct ?? 0}%`],
            ['Next run', data.zoho_crm.next_run],
          ]}
          testId="ci-health-crm"
        />
        <HealthCard
          title="Soul Engine"
          status={data.soul_engine.status}
          rows={[
            ['Profiles generated (lifetime)', data.soul_engine.profiles_generated_total ?? 0],
            ['Profiles this month', data.soul_engine.profiles_generated_this_month ?? 0],
            ['Outreach drafts this month', data.soul_engine.outreach_drafts_this_month ?? 0],
            ['Pushed to Zoho this month', data.soul_engine.pushed_to_zoho_this_month ?? 0],
            ['Coverage', `${data.soul_engine.coverage_pct ?? 0}%`],
            ['Last push', `${fmtTime(data.soul_engine.last_push_at)} · ${data.soul_engine.last_push_kind || '—'}${data.soul_engine.last_push_ticket ? ' · #' + data.soul_engine.last_push_ticket : ''}`],
          ]}
          testId="ci-health-soul"
        />
        <HealthCard
          title="SiteVault Backup"
          status={data.sitevault.status}
          rows={[
            ['Last backup', fmtTime(data.sitevault.last_backup)],
            ['Size', data.sitevault.size_mb ? `${data.sitevault.size_mb} MB` : '—'],
            ['Collections', data.sitevault.collections_count || '—'],
            ['Scope', data.sitevault.scope || '—'],
            ['Next run', data.sitevault.next_run],
          ]}
          testId="ci-health-sv"
        />
        <HealthCard
          title="Field Mappings"
          status={data.field_mappings.status}
          rows={[
            ['Active mappings', data.field_mappings.active_mappings || 0],
            ['Unmapped values needing attention', data.field_mappings.unmapped_values || 0],
          ]}
          testId="ci-health-mappings"
        />
      </div>
    </div>
  );
}

function HealthCard({ title, status, rows, testId }) {
  return (
    <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E5E1DC' }} data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] font-bold uppercase tracking-wider" style={{ color: '#2A7B88' }}>{title}</div>
        <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase inline-flex items-center gap-1" style={{
          background: STATUS_BG[status] || '#F3F4F6',
          color: STATUS_COLOR[status] || '#6B7280',
        }}>
          {status === 'green' ? <CheckCircle2 size={10} /> : status === 'amber' ? <AlertTriangle size={10} /> : <AlertCircle size={10} />}
          {status}
        </span>
      </div>
      <div className="space-y-1">
        {rows.map(([k, v], i) => (
          <div key={i} className="flex items-baseline justify-between gap-3 text-[11.5px]">
            <span style={{ color: '#6B7280' }}>{k}</span>
            <span className="text-right font-medium tabular-nums truncate" style={{ color: '#1B2A4A' }}>{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// 2. FIELD MAPPINGS PANEL
// ─────────────────────────────────────────────────────────────────────────
function MappingsPanel({ clientId, isCeo }) {
  const [list, setList] = useState([]);
  const [unmapped, setUnmapped] = useState([]);
  const [field, setField] = useState('');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null); // {field, zoho_value, display_name}
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const params = { client_id: clientId };
    if (field) params.field = field;
    const [r, u] = await Promise.all([
      api.get('/admin/field-mappings', { params }),
      api.get(`/admin/field-mappings/unmapped?client_id=${clientId}`),
    ]);
    setList(r.data?.mappings || []);
    setUnmapped(u.data?.unmapped || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId, field]);

  const save = async (m) => {
    setBusy(true);
    try {
      await api.post('/admin/field-mappings', m);
      await load();
      setEditing(null); setAdding(false);
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  const remove = async (m) => {
    if (!window.confirm(`Delete mapping ${m.field} · "${m.zoho_value}" → "${m.display_name}"?`)) return;
    setBusy(true);
    try {
      await api.delete('/admin/field-mappings', { params: { client_id: m.client_id, field: m.field, zoho_value: m.zoho_value } });
      await load();
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="ci-admin-mappings">
      {/* Unmapped queue */}
      {unmapped.length > 0 && (
        <div className="bg-white rounded-lg border-2 p-4" style={{ borderColor: '#A86B2C' }} data-testid="ci-admin-unmapped-queue">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={14} style={{ color: '#A86B2C' }} />
            <div className="text-[12px] font-bold" style={{ color: '#A86B2C' }}>{unmapped.length} unmapped value{unmapped.length === 1 ? '' : 's'} need your attention</div>
          </div>
          <div className="space-y-1.5">
            {unmapped.map((u, i) => (
              <div key={i} className="flex items-center gap-2 text-[11.5px]" style={{ color: '#1B2A4A' }}>
                <span className="px-1.5 py-0.5 rounded font-medium" style={{ background: '#F4F1EB', color: '#6B5439' }}>{u.field}</span>
                <span className="font-semibold flex-1 truncate">"{u.zoho_value}"</span>
                <span style={{ color: '#9CA3AF' }}>seen {u.seen_count}×</span>
                {isCeo && (
                  <button onClick={() => setEditing({ client_id: clientId, field: u.field, zoho_value: u.zoho_value, display_name: u.zoho_value })}
                    data-testid={`ci-admin-add-mapping-${u.field}`}
                    className="px-2 py-0.5 rounded text-[10px] font-medium text-white"
                    style={{ background: '#1C304A' }}>Add mapping</button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters + Add */}
      <div className="flex flex-wrap items-center gap-2">
        <select value={field} onChange={(e) => setField(e.target.value)} data-testid="ci-admin-mapping-field-filter"
          className="text-[11px] px-2 py-1 rounded border bg-white" style={{ borderColor: '#E5E1DC' }}>
          <option value="">All fields</option>
          {FIELD_OPTIONS.map(f => <option key={f.v} value={f.v}>{f.label}</option>)}
        </select>
        <span className="text-[11px]" style={{ color: '#9CA3AF' }}>{list.length} active mapping{list.length === 1 ? '' : 's'}</span>
        {isCeo && (
          <button onClick={() => setAdding(true)} data-testid="ci-admin-mapping-add"
            className="ml-auto px-3 py-1.5 rounded-md text-[11px] font-semibold text-white inline-flex items-center gap-1"
            style={{ backgroundColor: '#1C304A' }}>
            <Plus size={12} /> Add mapping
          </button>
        )}
      </div>

      {/* Add/Edit row */}
      {(adding || editing) && (
        <MappingEditRow
          initial={editing || { client_id: clientId, field: field || 'pillar', zoho_value: '', display_name: '' }}
          onCancel={() => { setEditing(null); setAdding(false); }}
          onSave={save}
          busy={busy}
        />
      )}

      {/* Table */}
      <div className="bg-white rounded-lg border overflow-hidden" style={{ borderColor: '#E5E1DC' }}>
        <table className="w-full text-[11.5px]">
          <thead style={{ background: '#F7F5F2' }}>
            <tr className="text-left" style={{ color: '#6B7280' }}>
              <th className="py-2 px-3">Field</th>
              <th className="py-2 px-3">Zoho value</th>
              <th className="py-2 px-3">→ Display name</th>
              <th className="py-2 px-3">Updated by</th>
              <th className="py-2 px-3"></th>
            </tr>
          </thead>
          <tbody data-testid="ci-admin-mapping-tbody">
            {list.map((m, i) => (
              <tr key={i} className="border-t" style={{ borderColor: '#F0EEEA' }}>
                <td className="py-2 px-3">
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ background: '#F4F1EB', color: '#6B5439' }}>{m.field}</span>
                </td>
                <td className="py-2 px-3 font-medium" style={{ color: '#1B2A4A' }}>{m.zoho_value}</td>
                <td className="py-2 px-3" style={{ color: '#2A7B88' }}>{m.display_name}</td>
                <td className="py-2 px-3 text-[10px]" style={{ color: '#9CA3AF' }}>{m.updated_by || '—'}</td>
                <td className="py-2 px-3 text-right">
                  {isCeo && (
                    <>
                      <button onClick={() => setEditing(m)} data-testid={`ci-admin-mapping-edit-${i}`} className="p-1 hover:bg-gray-100 rounded mr-1"><Edit2 size={11} style={{ color: '#6B7280' }} /></button>
                      <button onClick={() => remove(m)} data-testid={`ci-admin-mapping-delete-${i}`} className="p-1 hover:bg-gray-100 rounded"><Trash2 size={11} style={{ color: '#A32D2D' }} /></button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr><td colSpan={5} className="py-4 text-center text-[11px]" style={{ color: '#9CA3AF' }}>No mappings yet for this client.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MappingEditRow({ initial, onCancel, onSave, busy }) {
  const [m, setM] = useState(initial);
  return (
    <div className="bg-white rounded-lg border-2 p-3 grid grid-cols-12 gap-2 items-center" style={{ borderColor: '#1C304A' }} data-testid="ci-admin-mapping-edit-row">
      <select value={m.field} onChange={(e) => setM({ ...m, field: e.target.value })}
        data-testid="ci-admin-mapping-field-input"
        className="col-span-3 text-[11px] px-2 py-1.5 rounded border bg-white" style={{ borderColor: '#E5E1DC' }}>
        {FIELD_OPTIONS.map(f => <option key={f.v} value={f.v}>{f.label}</option>)}
      </select>
      <input value={m.zoho_value} onChange={(e) => setM({ ...m, zoho_value: e.target.value })}
        placeholder="Zoho value (exact)"
        data-testid="ci-admin-mapping-zoho-input"
        className="col-span-4 text-[11.5px] px-2 py-1.5 rounded border bg-white" style={{ borderColor: '#E5E1DC' }} />
      <input value={m.display_name} onChange={(e) => setM({ ...m, display_name: e.target.value })}
        placeholder="ScaleBoard display name"
        data-testid="ci-admin-mapping-display-input"
        className="col-span-3 text-[11.5px] px-2 py-1.5 rounded border bg-white" style={{ borderColor: '#E5E1DC' }} />
      <button onClick={() => onSave(m)} disabled={busy || !m.zoho_value || !m.display_name}
        data-testid="ci-admin-mapping-save"
        className="col-span-1 px-2 py-1.5 rounded text-[11px] font-semibold text-white inline-flex items-center justify-center gap-1 disabled:opacity-50"
        style={{ backgroundColor: '#1C304A' }}>
        <Save size={11} /> Save
      </button>
      <button onClick={onCancel} data-testid="ci-admin-mapping-cancel"
        className="col-span-1 px-2 py-1.5 rounded text-[11px] inline-flex items-center justify-center gap-1 border bg-white" style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>
        <X size={11} /> Cancel
      </button>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// 3. MEMBERS PANEL — search, edit, export
// ─────────────────────────────────────────────────────────────────────────
function MembersPanel({ clientId, isCeo }) {
  const [members, setMembers] = useState([]);
  const [meta, setMeta] = useState({ total_members: 0, soul_completed: 0 });
  const [q, setQ] = useState('');
  const [tier, setTier] = useState('');
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (q) params.q = q;
      if (tier) params.tier = tier;
      const r = await api.get(`/soul-engine/${clientId}/members`, { params });
      setMembers(r.data?.members || []);
      setMeta({ total_members: r.data?.total_members || 0, soul_completed: r.data?.soul_completed || 0 });
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [clientId, q, tier]);

  const exportCsv = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/soul-engine/${clientId}/members.csv?include_soul=true`;
    const token = localStorage.getItem('access_token') || '';
    // Browsers won't send Authorization header on a regular link click — so
    // we fetch it and trigger a download manually.
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = window.URL.createObjectURL(blob);
        a.download = `members_${clientId}_${new Date().toISOString().slice(0,10)}.csv`;
        a.click();
      });
  };

  const saveEdit = async (mid, patch) => {
    try {
      await api.patch(`/soul-engine/${clientId}/members/${mid}`, patch);
      setEditing(null);
      await load();
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  };

  return (
    <div className="space-y-3" data-testid="ci-admin-members">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center gap-1 px-2 py-1 rounded border bg-white" style={{ borderColor: '#E5E1DC' }}>
          <Search size={11} style={{ color: '#9CA3AF' }} />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search name or email…"
            data-testid="ci-admin-member-search"
            className="text-[12px] outline-none bg-transparent w-56" />
        </div>
        <select value={tier} onChange={(e) => setTier(e.target.value)} data-testid="ci-admin-member-tier-filter"
          className="text-[11px] px-2 py-1 rounded border bg-white" style={{ borderColor: '#E5E1DC' }}>
          <option value="">All tiers</option>
          <option value="RR">RR</option>
          <option value="RRS">RRS</option>
        </select>
        <span className="text-[11px]" style={{ color: '#9CA3AF' }}>
          {members.length} of {(meta.total_members || 0).toLocaleString('en-IN')} members
        </span>
        <button onClick={exportCsv} data-testid="ci-admin-members-export"
          className="ml-auto px-3 py-1.5 rounded-md text-[11px] font-semibold text-white inline-flex items-center gap-1"
          style={{ backgroundColor: '#2A7B88' }}>
          <Download size={12} /> Export CSV
        </button>
      </div>

      <div className="bg-white rounded-lg border overflow-hidden" style={{ borderColor: '#E5E1DC' }}>
        <div className="overflow-y-auto" style={{ maxHeight: 540 }}>
          <table className="w-full text-[11.5px]">
            <thead className="sticky top-0" style={{ background: '#F7F5F2' }}>
              <tr className="text-left" style={{ color: '#6B7280' }}>
                <th className="py-2 px-3">Name</th>
                <th className="py-2 px-3">City</th>
                <th className="py-2 px-3">Tier</th>
                <th className="py-2 px-3">Email</th>
                <th className="py-2 px-3">Phone</th>
                <th className="py-2 px-3">DOB</th>
                <th className="py-2 px-3">Anniv</th>
                <th className="py-2 px-3">Soul</th>
                <th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody data-testid="ci-admin-members-tbody">
              {loading ? (
                <tr><td colSpan={9} className="py-4 text-center text-[11px]" style={{ color: '#9CA3AF' }}>Loading…</td></tr>
              ) : members.length === 0 ? (
                <tr><td colSpan={9} className="py-4 text-center text-[11px]" style={{ color: '#9CA3AF' }}>No members match.</td></tr>
              ) : members.map((m, i) =>
                editing === m.zoho_contact_id
                  ? <MemberEditRow key={i} member={m} onCancel={() => setEditing(null)} onSave={(p) => saveEdit(m.zoho_contact_id, p)} />
                  : (
                    <tr key={i} className="border-t hover:bg-gray-50" style={{ borderColor: '#F0EEEA' }}>
                      <td className="py-2 px-3 font-medium" style={{ color: '#1B2A4A' }}>{m.name || '—'}</td>
                      <td className="py-2 px-3" style={{ color: '#6B7280' }}>{m.city || '—'}</td>
                      <td className="py-2 px-3 font-semibold" style={{ color: m.member_tier === 'RR' ? '#1C304A' : '#5C5652' }}>{m.member_tier || '—'}</td>
                      <td className="py-2 px-3" style={{ color: '#6B7280' }}>{m.email || '—'}</td>
                      <td className="py-2 px-3 tabular-nums" style={{ color: '#6B7280' }}>{m.phone || '—'}</td>
                      <td className="py-2 px-3 tabular-nums" style={{ color: '#6B7280' }}>{(m.date_of_birth || '').slice(0,10) || '—'}</td>
                      <td className="py-2 px-3 tabular-nums" style={{ color: '#6B7280' }}>{(m.date_of_anniversary || '').slice(0,10) || '—'}</td>
                      <td className="py-2 px-3">{m.soul_form_completed ? <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: '#E5F4ED', color: '#1f5736' }}>✓</span> : '—'}</td>
                      <td className="py-2 px-3 text-right">
                        {isCeo && <button onClick={() => setEditing(m.zoho_contact_id)} data-testid={`ci-admin-member-edit-${m.zoho_contact_id}`} className="p-1 hover:bg-gray-100 rounded"><Edit2 size={11} style={{ color: '#6B7280' }} /></button>}
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-[10.5px] italic" style={{ color: '#9CA3AF' }}>
        Edits set <code>_manual_override: true</code> — the next nightly CRM sync skips overridden fields. Edit the value in Zoho directly to clear the override.
      </p>
    </div>
  );
}

function MemberEditRow({ member, onCancel, onSave }) {
  const [m, setM] = useState({ ...member });
  const inp = (k, w = 'w-32') => (
    <input value={m[k] || ''} onChange={(e) => setM({ ...m, [k]: e.target.value })}
      data-testid={`ci-admin-member-edit-${k}`}
      className={`text-[11.5px] px-1.5 py-1 rounded border bg-white ${w}`} style={{ borderColor: '#E5E1DC' }} />
  );
  return (
    <tr className="border-t" style={{ borderColor: '#1C304A', background: '#F4F1EB' }}>
      <td className="py-1.5 px-3">{inp('name', 'w-44')}</td>
      <td className="py-1.5 px-3">{inp('city')}</td>
      <td className="py-1.5 px-3">
        <select value={m.member_tier || ''} onChange={(e) => setM({ ...m, member_tier: e.target.value })}
          data-testid="ci-admin-member-edit-tier"
          className="text-[11.5px] px-1.5 py-1 rounded border bg-white w-16" style={{ borderColor: '#E5E1DC' }}>
          <option value="">—</option><option>RR</option><option>RRS</option>
        </select>
      </td>
      <td className="py-1.5 px-3">{inp('email', 'w-44')}</td>
      <td className="py-1.5 px-3">{inp('phone')}</td>
      <td className="py-1.5 px-3">{inp('date_of_birth', 'w-28')}</td>
      <td className="py-1.5 px-3">{inp('date_of_anniversary', 'w-28')}</td>
      <td className="py-1.5 px-3"></td>
      <td className="py-1.5 px-3 text-right whitespace-nowrap">
        <button onClick={() => onSave({ name: m.name, city: m.city, member_tier: m.member_tier, email: m.email, phone: m.phone, date_of_birth: m.date_of_birth, date_of_anniversary: m.date_of_anniversary })}
          data-testid="ci-admin-member-edit-save"
          className="p-1 hover:bg-gray-100 rounded mr-1"><Save size={12} style={{ color: '#1f5736' }} /></button>
        <button onClick={onCancel} data-testid="ci-admin-member-edit-cancel"
          className="p-1 hover:bg-gray-100 rounded"><X size={12} style={{ color: '#A32D2D' }} /></button>
      </td>
    </tr>
  );
}


// ────────────────────────────────────────────────────────────────────────────
// Member Care Engine panel — dry-run preview, run-now, recent runs
// ────────────────────────────────────────────────────────────────────────────
function MemberCarePanel({ clientId, isCeo }) {
  const [config, setConfig] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);
  const [drafts, setDrafts] = useState([]);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [cfg, rs] = await Promise.all([
        api.get('/member-care/config'),
        api.get(`/member-care/${clientId}/runs?limit=20`),
      ]);
      setConfig(cfg.data);
      setRuns(rs.data?.runs || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load Member Care config');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  const runNow = async () => {
    if (!confirm(`This will draft via Claude and push LIVE Zoho Desk tickets for ${clientId.toUpperCase()}. Continue?`)) return;
    setBusy(true); setError(null);
    try {
      await api.post(`/member-care/${clientId}/run-now`);
      alert('Live sweep started. Check "Recent runs" below — refreshes every 10s.');
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Run failed');
    } finally { setBusy(false); }
  };

  const openRun = async (runId) => {
    setSelectedRun(runId); setDrafts([]);
    try {
      const res = await api.get(`/member-care/runs/${runId}/drafts?limit=200`);
      setDrafts(res.data?.drafts || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load drafts');
    }
  };

  useEffect(() => {
    const iv = setInterval(() => {
      if (runs.some(r => r.status === 'running')) load();
    }, 10000);
    return () => clearInterval(iv);
    /* eslint-disable-next-line */
  }, [runs]);

  if (loading) return <div className="p-8 text-center text-[12px]" style={{ color: '#6B7280' }}>Loading Member Care config…</div>;

  const isEnabledClient = config?.clients?.includes(clientId);

  return (
    <div className="space-y-4" data-testid="mce-panel">
      <MemberCareCheatGuide />
      {error && (
        <div className="p-3 text-[12px] rounded" style={{ background: STATUS_BG.red, color: STATUS_COLOR.red }}>
          {error}
        </div>
      )}

      {/* Header card */}
      <div className="p-4 rounded border" style={{ background: 'white', borderColor: '#E5E1DC' }}>
        <div className="flex items-center gap-3">
          <Heart size={18} style={{ color: '#C9A66B' }} />
          <div className="flex-1">
            <div className="text-[14px] font-semibold" style={{ color: '#1B2A4A' }}>Member Care Engine</div>
            <div className="text-[11.5px] mt-0.5" style={{ color: '#6B7280' }}>
              One nightly sweep · {config?.next_sweep_at_ist} · 12 triggers · concurrency 3
            </div>
          </div>
          {isEnabledClient ? (
            <span className="text-[10px] px-2 py-1 rounded" style={{ background: STATUS_BG.green, color: STATUS_COLOR.green }}>
              ENABLED
            </span>
          ) : (
            <span className="text-[10px] px-2 py-1 rounded" style={{ background: STATUS_BG.amber, color: STATUS_COLOR.amber }}>
              {clientId.toUpperCase()} not in sweep
            </span>
          )}
        </div>

        {isEnabledClient && (
          <div className="flex items-center gap-2 mt-3">
            {isCeo && (
              <button
                onClick={runNow}
                disabled={busy}
                data-testid="mce-run-now-btn"
                className="text-[12px] px-3 py-1.5 rounded border disabled:opacity-50"
                style={{ background: '#1B2A4A', borderColor: '#1B2A4A', color: '#EEECE1' }}>
                <Send size={12} className="inline mr-1" />
                Run live now (CEO)
              </button>
            )}
            <button
              onClick={load}
              data-testid="mce-refresh-btn"
              className="text-[12px] p-1.5 rounded border"
              style={{ background: 'white', borderColor: '#E5E1DC' }}>
              <RefreshCw size={12} />
            </button>
          </div>
        )}
      </div>

      {/* Dipali's MCE control panel — live counter / breakdown / preview list. */}
      {isEnabledClient && (
        <MCERunPanel clientId={clientId} />
      )}

      {/* Triggers reference */}
      {config?.triggers && (
        <details className="rounded border p-3" style={{ background: '#FAF9F6', borderColor: '#E5E1DC' }}>
          <summary className="text-[12px] cursor-pointer" style={{ color: '#1B2A4A' }}>
            12 triggers monitored →
          </summary>
          <div className="grid grid-cols-2 gap-2 mt-3">
            {config.triggers.map(t => (
              <div key={t.trigger} className="text-[11px] p-2 rounded" style={{ background: 'white' }}>
                <div className="font-medium" style={{ color: '#1B2A4A' }}>{t.trigger}</div>
                <div style={{ color: '#6B7280' }}>{t.goal}</div>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Runs table */}
      <div className="rounded border overflow-hidden" style={{ background: 'white', borderColor: '#E5E1DC' }}>
        <div className="px-3 py-2 text-[11px] font-semibold border-b" style={{ color: '#6B7280', background: '#FAF9F6', borderColor: '#E5E1DC' }}>
          Recent sweeps · {clientId.toUpperCase()}
        </div>
        {runs.length === 0 ? (
          <div className="p-6 text-center text-[12px]" style={{ color: '#6B7280' }}>
            No sweeps yet. First nightly run fires at 03:00 IST.
          </div>
        ) : (
          <table className="w-full text-[11.5px]">
            <thead>
              <tr style={{ background: '#FAF9F6', color: '#6B7280' }}>
                <th className="text-left px-3 py-2 font-medium">Date</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-right px-3 py-2 font-medium">Hits</th>
                <th className="text-right px-3 py-2 font-medium">Pushed</th>
                <th className="text-right px-3 py-2 font-medium">Failed</th>
                <th className="text-left px-3 py-2 font-medium">Dry</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {runs.map(r => (
                <tr key={r.run_id} className="border-t" style={{ borderColor: '#F0ECE6' }}>
                  <td className="px-3 py-2 tabular-nums" style={{ color: '#1B2A4A' }}>{r.anchor_date}</td>
                  <td className="px-3 py-2">
                    <span className="px-2 py-0.5 rounded text-[10px]" style={{
                      background: r.status === 'ready' ? STATUS_BG.green : STATUS_BG.amber,
                      color: r.status === 'ready' ? STATUS_COLOR.green : STATUS_COLOR.amber,
                    }}>{r.status}</span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums" style={{ color: '#1B2A4A' }}>{r.summary?.total_hits ?? '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums" style={{ color: '#1f5736' }}>{r.summary?.pushed ?? '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums" style={{ color: (r.summary?.failed || 0) > 0 ? STATUS_COLOR.red : '#6B7280' }}>{r.summary?.failed ?? '—'}</td>
                  <td className="px-3 py-2 text-[10px]" style={{ color: '#6B7280' }}>{r.dry_run ? 'dry' : 'live'}</td>
                  <td className="px-3 py-2 text-right">
                    {!r.dry_run && (r.summary?.pushed ?? 0) > 0 && (
                      <button
                        onClick={() => openRun(r.run_id)}
                        data-testid={`mce-run-open-${r.run_id}`}
                        className="text-[11px] underline" style={{ color: '#2A7B88' }}>
                        Drafts
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Drafts drawer */}
      {selectedRun && (
        <div className="rounded border overflow-hidden" style={{ background: 'white', borderColor: '#E5E1DC' }}>
          <div className="px-3 py-2 flex items-center gap-2 border-b" style={{ background: '#FAF9F6', borderColor: '#E5E1DC' }}>
            <div className="text-[11px] font-semibold" style={{ color: '#6B7280' }}>
              Drafts for run {selectedRun.slice(0,8)}… · {drafts.length}
            </div>
            <button
              onClick={() => { setSelectedRun(null); setDrafts([]); }}
              data-testid="mce-drafts-close"
              className="ml-auto text-[11px] p-1 hover:bg-gray-100 rounded"><X size={12} /></button>
          </div>
          {drafts.length === 0 ? (
            <div className="p-4 text-center text-[11.5px]" style={{ color: '#6B7280' }}>No drafts on file.</div>
          ) : (
            <table className="w-full text-[11.5px]">
              <thead>
                <tr style={{ background: '#FAF9F6', color: '#6B7280' }}>
                  <th className="text-left px-3 py-2 font-medium">Member</th>
                  <th className="text-left px-3 py-2 font-medium">Trigger</th>
                  <th className="text-left px-3 py-2 font-medium">Urgency</th>
                  <th className="text-left px-3 py-2 font-medium">Subject</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {drafts.map(d => (
                  <tr key={d.zoho_ticket_id || d.member_id} className="border-t" style={{ borderColor: '#F0ECE6' }}>
                    <td className="px-3 py-2" style={{ color: '#1B2A4A' }}>{d.member_name || '—'}</td>
                    <td className="px-3 py-2 text-[10px]" style={{ color: '#6B7280' }}>{d.trigger}</td>
                    <td className="px-3 py-2 text-[10px]" style={{ color: d.urgency === 'high' ? STATUS_COLOR.red : '#6B7280' }}>{d.urgency}</td>
                    <td className="px-3 py-2 text-[11px]" style={{ color: '#1B2A4A' }}>{d.subject}</td>
                    <td className="px-3 py-2 text-right">
                      {d.zoho_ticket_url && (
                        <a href={d.zoho_ticket_url} target="_blank" rel="noopener noreferrer"
                           className="text-[11px] underline" style={{ color: '#2A7B88' }}>
                          #{d.zoho_ticket_number} →
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────
// Picklist Aliases panel — self-service Zoho-merge management
//
// When the team merges two values in Zoho's picklist (e.g. "Concierge
// (General)" → "Concierge"), Zoho doesn't always retroactively rewrite
// historical tickets. The dashboard then shows two rows for the same
// merged thing. This panel lets Dipali manage those alias rules without
// touching code or running a re-sync.
// ────────────────────────────────────────────────────────────────────────
function PicklistAliasesPanel({ clientId, isCeo }) {
  const [items, setItems] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [knownPillars, setKnownPillars] = useState([]);
  const [knownBenefits, setKnownBenefits] = useState([]);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ kind: 'pillar', from: '', to: '', enabled: true });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = async () => {
    setBusy(true);
    setErr('');
    try {
      const [a, kv] = await Promise.all([
        api.get('/customer-intelligence/_admin/picklist-aliases'),
        api.get('/customer-intelligence/_admin/picklist-known-values', { params: { client_id: clientId } }),
      ]);
      setItems(a.data?.items || []);
      setDefaults(a.data?.defaults || []);
      setKnownPillars(kv.data?.pillars || []);
      setKnownBenefits(kv.data?.benefits || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  const save = async () => {
    if (!draft.from.trim() || !draft.to.trim()) {
      setErr('Both "from" and "to" are required.');
      return;
    }
    if (draft.from.trim() === draft.to.trim()) {
      setErr('"from" and "to" must differ.');
      return;
    }
    setBusy(true); setErr('');
    try {
      await api.post('/customer-intelligence/_admin/picklist-aliases', {
        kind: draft.kind,
        from: draft.from.trim(),
        to: draft.to.trim(),
        enabled: true,
      });
      setAdding(false);
      setDraft({ kind: 'pillar', from: '', to: '', enabled: true });
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const remove = async (aliasId) => {
    if (!window.confirm('Remove this merge rule? The dashboard will start showing both values again.')) return;
    setBusy(true);
    try {
      await api.delete(`/customer-intelligence/_admin/picklist-aliases/${aliasId}`);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const knownFor = (kind) => kind === 'pillar' ? knownPillars : knownBenefits;
  const canonical = (kind) => {
    // Suggest "to" values that ALREADY exist in the data — so merging
    // into a typo is impossible.
    const seen = new Set(knownFor(kind));
    [...defaults, ...items].forEach(a => { if (a.kind === kind) seen.add(a.to); });
    return Array.from(seen).sort();
  };

  return (
    <div className="space-y-3" data-testid="ci-admin-aliases">
      <div className="flex items-start gap-3 p-3 rounded" style={{ background: '#FFF8EB', border: '1px solid #E5C9A1' }}>
        <GitMerge size={14} style={{ color: '#A86B2C', marginTop: 2 }} />
        <div className="text-[11.5px]" style={{ color: '#6B5439' }}>
          When you merge a picklist value in Zoho (e.g. "Concierge (General)" → "Concierge"),
          historical tickets keep the old name. Add a merge rule below and the dashboard
          collapses them instantly — no re-sync needed.
        </div>
      </div>

      {err && (
        <div className="text-[11.5px] p-2 rounded" style={{ background: '#FFEFEF', color: '#B5253E', border: '1px solid #F4C7C7' }}>
          {err}
        </div>
      )}

      <div className="flex items-center gap-2">
        <span className="text-[11.5px] font-semibold" style={{ color: '#1B2A4A' }}>Merge rules</span>
        <span className="text-[11px]" style={{ color: '#9CA3AF' }}>
          {items.length} active · {defaults.length} default
        </span>
        {isCeo && (
          <button
            onClick={() => { setAdding(true); setErr(''); }}
            data-testid="ci-admin-alias-add"
            className="ml-auto px-3 py-1.5 rounded-md text-[11px] font-semibold text-white inline-flex items-center gap-1"
            style={{ backgroundColor: '#1C304A' }}>
            <Plus size={12} /> Add merge rule
          </button>
        )}
      </div>

      {adding && (
        <div className="rounded border p-3 space-y-2" style={{ background: '#FAF9F6', borderColor: '#E5E1DC' }} data-testid="ci-admin-alias-edit">
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={draft.kind}
              onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
              data-testid="ci-admin-alias-kind"
              className="text-[11.5px] px-2 py-1 rounded border bg-white" style={{ borderColor: '#E5E1DC' }}>
              <option value="pillar">Pillar</option>
              <option value="benefit">Benefit</option>
            </select>

            <input
              list="alias-from-options"
              value={draft.from}
              onChange={(e) => setDraft({ ...draft, from: e.target.value })}
              placeholder="Old value (in Zoho before merge)"
              data-testid="ci-admin-alias-from"
              className="text-[11.5px] px-2 py-1 rounded border flex-1 min-w-[200px]" style={{ borderColor: '#E5E1DC' }} />
            <datalist id="alias-from-options">
              {knownFor(draft.kind).map(v => <option key={v} value={v} />)}
            </datalist>

            <ArrowRight size={14} style={{ color: '#9CA3AF' }} />

            <input
              list="alias-to-options"
              value={draft.to}
              onChange={(e) => setDraft({ ...draft, to: e.target.value })}
              placeholder="New canonical value"
              data-testid="ci-admin-alias-to"
              className="text-[11.5px] px-2 py-1 rounded border flex-1 min-w-[200px]" style={{ borderColor: '#E5E1DC' }} />
            <datalist id="alias-to-options">
              {canonical(draft.kind).map(v => <option key={v} value={v} />)}
            </datalist>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={save}
              disabled={busy}
              data-testid="ci-admin-alias-save"
              className="px-3 py-1.5 rounded-md text-[11px] font-semibold text-white inline-flex items-center gap-1 disabled:opacity-50"
              style={{ backgroundColor: '#2A7B88' }}>
              <Save size={11} /> Save merge rule
            </button>
            <button
              onClick={() => { setAdding(false); setErr(''); }}
              data-testid="ci-admin-alias-cancel"
              className="px-3 py-1.5 rounded-md text-[11px] font-semibold inline-flex items-center gap-1"
              style={{ backgroundColor: '#F4F1EB', color: '#6B5439' }}>
              <X size={11} /> Cancel
            </button>
            <span className="text-[10.5px]" style={{ color: '#9CA3AF' }}>
              Tip: type a new value or pick from Zoho's existing values.
            </span>
          </div>
        </div>
      )}

      {/* Active rules */}
      <div className="rounded border overflow-hidden" style={{ background: 'white', borderColor: '#E5E1DC' }}>
        <div className="px-3 py-2 text-[10.5px] font-bold uppercase tracking-wider"
              style={{ background: '#FAF9F6', color: '#6B5439' }}>
          Active rules ({items.length})
        </div>
        {items.length === 0 && defaults.length === 0 ? (
          <div className="p-4 text-center text-[11.5px]" style={{ color: '#9CA3AF' }}>No merge rules yet. Click "Add merge rule" to create one.</div>
        ) : (
          <table className="w-full text-[11.5px]">
            <thead>
              <tr style={{ background: '#FAF9F6', color: '#6B7280' }}>
                <th className="text-left px-3 py-2 font-medium" style={{ width: 80 }}>Kind</th>
                <th className="text-left px-3 py-2 font-medium">From (old value)</th>
                <th className="text-left px-3 py-2 font-medium">To (canonical)</th>
                <th className="text-left px-3 py-2 font-medium" style={{ width: 110 }}>Status</th>
                <th style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map(a => (
                <tr key={a.id || `${a.kind}-${a.from}`} className="border-t" style={{ borderColor: '#F0ECE6' }}>
                  <td className="px-3 py-2"><span className="px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ background: '#F4F1EB', color: '#6B5439' }}>{a.kind}</span></td>
                  <td className="px-3 py-2 font-medium" style={{ color: '#1B2A4A' }}>"{a.from}"</td>
                  <td className="px-3 py-2" style={{ color: '#2A7B88' }}>"{a.to}"</td>
                  <td className="px-3 py-2 text-[10.5px]" style={{ color: a.enabled === false ? '#9CA3AF' : '#2A7B88' }}>
                    {a.enabled === false ? 'disabled' : 'active'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {isCeo && (
                      <button onClick={() => remove(a.id)}
                        data-testid={`ci-admin-alias-delete-${a.id}`}
                        title="Remove this merge rule"
                        className="p-1 rounded hover:bg-red-50">
                        <Trash2 size={11} style={{ color: '#B5253E' }} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {defaults.map(a => (
                <tr key={a.id} className="border-t" style={{ borderColor: '#F0ECE6', background: '#FAFAF7' }}>
                  <td className="px-3 py-2"><span className="px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ background: '#F4F1EB', color: '#6B5439' }}>{a.kind}</span></td>
                  <td className="px-3 py-2 font-medium" style={{ color: '#1B2A4A' }}>"{a.from}"</td>
                  <td className="px-3 py-2" style={{ color: '#2A7B88' }}>"{a.to}"</td>
                  <td className="px-3 py-2 text-[10.5px]" style={{ color: '#9CA3AF' }}>seeded</td>
                  <td className="px-3 py-2 text-right">
                    <span className="text-[10px]" style={{ color: '#9CA3AF' }}>locked</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
